import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TeamLeaderSelector from "work/work-agreements/teams/vendor-team/_views/leader/team-leader-selector";
import VendorTeamSelection from "work/work-agreements/teams/vendor-team/_views/marketplace/marketplace-vendor-team-selector";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const FromMarketplaceButton = styled(Button)(() => ({
  width: "fit-content",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
  "&:first-of-type": {
    marginRight: theme.spacing(3),
  },
  [theme.breakpoints.down("lg")]: {
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecorationColor: theme.palette.common.black,
}));
const RemoveIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));
const TeamLeaderInput = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(25),
}));
const TeamLoader = styled(Loader)(() => ({
  justifyContent: 'flex-start'
}));

type EditVendorTeamProps = {
  className?: string;
  leader: EntityMember | null;
  members: EntityMember[];
  disableEditing?: boolean;
  navigate?: NavigateFunction;
  disableComments: boolean;
  hideCommentButton?: boolean;
  activeReviewField?: ProposalField;
  onLeaderUpdated?: (leader: EntityMember | null) => void;
  onMemberAdded?: (member: EntityMember) => void;
  onMemberRemoved?: (userId?: Guid) => void;
  onMarketplaceTeamSelected?: (team: MarketplaceTeam) => void;
  commentThreads?: CommentThread[];
  commentsToolTip: string;
  onCommentsClicked?: (proposalField: ProposalField, name?: string) => void;
};

export default function EditVendorTeam(props: Readonly<EditVendorTeamProps>) {
  const {
    className,
    leader,
    members,
    commentThreads,
    disableEditing,
    navigate,
    disableComments,
    hideCommentButton,
    commentsToolTip,
    activeReviewField,
    onCommentsClicked,
    onLeaderUpdated,
    onMemberAdded,
    onMemberRemoved,
    onMarketplaceTeamSelected,
  } = props;

  const memberUserIds: string[] = useMemo(() => {
    return members.map((member) => member.userId.value);
  }, [members]);

  const individualsToFetch = useMemo(() => {
    const ids = new Set<string>();
    if (leader) ids.add(leader.userId.value);
    memberUserIds.forEach((userId) => ids.add(userId));
    return ids;
  }, [leader, memberUserIds]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualsToFetch.size ? { userIds: individualsToFetch } : skipToken
  );

  const individuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const individualMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    individuals.forEach((individual) => {
      if (individual.userId?.value) {
        map.set(individual.userId.value, individual);
      }
    });
    return map;
  }, [individuals]);

  const leaderIndividual: Individual | undefined = useMemo(() => {
    if (!leader) return undefined;
    return individualMap.get(leader.userId.value);
  }, [leader, individualMap]);

  const memberIndividuals: Individual[] = useMemo(() => {
    if (memberUserIds.length === 0) return [];
    return memberUserIds
      .map((userId) => individualMap.get(userId))
      .filter((individual): individual is Individual => individual !== undefined);
  }, [memberUserIds, individualMap]);

  const userPermissions = useSelector(getSessionPermissions);

  const [selectedLeader, setSelectedLeader] = React.useState<Individual | null>(leaderIndividual ?? null);
  const [selectedMembers, setSelectedMembers] = React.useState<Individual[]>(memberIndividuals);
  const [teamLeaderSelectorAnchor, setTeamLeaderSelectorAnchor] = React.useState<HTMLDivElement>();

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const session = useSession();

  useEffect(() => {
    if (!leaderIndividual) return;
    setSelectedLeader(leaderIndividual);
  }, [leaderIndividual]);

  useEffect(() => {
    if (!memberIndividuals) return;
    setSelectedMembers(memberIndividuals ?? []);
  }, [memberIndividuals]);

  /**
   * Checks if the user with the given id is already selected or invited to the vendor-team
   *
   * @param userId Id of the user to check
   * @returns True if the user is already selected or invited to the vendor-team, false otherwise
   */
  function getIsMemberAlreadyOnTeam(userId?: Guid): boolean {
    if (!userId) return false;

    const memberUserIds = selectedMembers?.map((member) => member.userId);

    return (
      (memberUserIds?.some((member) => member?.isEqualTo(userId)) ?? false) ||
      (selectedLeader?.userId?.isEqualTo(userId) ?? false)
    );
  }

  function getSelectedTeamMemberUserIds(): Guid[] {
    const userIds: Guid[] = selectedMembers?.map((member) => member.userId) ?? [];
    if (selectedLeader?.userId) {
      userIds.push(selectedLeader.userId);
    }
    return userIds;
  }

  /**
   * Handles what happens when add leader is clicked
   */
  function beginLeaderSelection() {
    openDialog({
      title: "Select Leader",
      component: (
        <VendorTeamMemberSelection
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new leader has been selected
   *
   * @param leader The new leader to replace the current leader with
   */
  async function handleNewLeaderSelected(leader: Individual) {
    if (selectedLeader) {
      const response = await confirm({
        title: "Replace Leader?",
        message: `Do you want to replace the current team leader with ${leader.fullName}?`,
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    setSelectedLeader(leader);

    if (selectedMembers?.find((member) => member.userId?.isEqualTo(leader.userId))) {
      setSelectedMembers(selectedMembers?.filter((member) => !member.userId?.isEqualTo(leader.userId)));
      onLeaderUpdated?.(leader.toEntityMember());
      return;
    }

    onLeaderUpdated?.(leader?.toEntityMember());
  }

  async function handleCoworkerSelectedAsLeader(leader?: Individual) {
    if (!leader) {
      setSelectedLeader(null);
      return;
    }

    setSelectedLeader(leader);
    onLeaderUpdated?.(leader?.toEntityMember());
  }

  function handleSelectTeamLeaderClicked(event: React.MouseEvent<HTMLDivElement>) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setTeamLeaderSelectorAnchor(inputElements.length > 0 ? inputElements[0] : event.currentTarget);
  }

  /**
   * Handles what happens when add member is clicked
   */
  function beginMemberSelection() {
    openDialog({
      title: "Select Member",
      component: (
        <VendorTeamMemberSelection
          navigate={navigate}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new member has been selected
   *
   * @param teamMember The new member to add to the vendor-team
   */
  function handleNewMemberSelected(teamMember: Individual) {
    if (getIsMemberAlreadyOnTeam(teamMember.userId)) {
      enqueueSnackbar("Selected user is already on vendor-team", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 7500,
      });
      return;
    }

    onMemberAdded?.(teamMember.toEntityMember());
  }

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   */
  async function handleLeaderRemoved() {
    const response = await confirm({
      title: "Remove Leader?",
      message: `Do you want to remove the leader from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    setSelectedLeader(null);
    onLeaderUpdated?.(null);
  }

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param userId The userId of the member to remove
   */
  async function handleMemberRemoved(userId?: Guid) {
    if (!userId) return;

    const response = await confirm({
      title: "Remove Member?",
      message: `Do you want to remove the member from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    const newMembers = selectedMembers?.filter((member) => !member.userId?.isEqualTo(userId));
    setSelectedMembers(newMembers);
    onMemberRemoved?.(userId);
  }

  async function handleRemoveAllMembers() {
    const response = await confirm({
      title: "Remove All Members?",
      message: "Do you want to remove all members from the vendor-team?",
      okButtonText: "Remove All",
    });

    if (response === ConfirmResponse.Cancel) return;

    setSelectedMembers([]);
    onMemberRemoved?.();
  }

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  function beginMarketplaceTeamSelection() {
    openDialog({
      title: "Select Marketplace Team",
      component: (
        <VendorTeamSelection 
          onNewSelection={handleMarketplaceTeamSelected} 
          closeDialog={popDialog} 
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  function renderLeader(leader: Individual, allowDelete?: boolean) {
    return (
      <ListItem
        key={leader.userId.value ?? Guid.generate().value}
        secondaryAction={
          <>
            {!disableEditing && allowDelete && (
              <Tooltip title="Remove Leader">
                <span>
                  <IconButton onClick={handleLeaderRemoved} size="medium">
                    <RemoveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!hideCommentButton && (
              <CommentsButton
                field={ProposalField.TeamLeader}
                isActive={activeReviewField?.isEqualTo(ProposalField.TeamLeader)}
                disabled={disableComments}
                toolTip={commentsToolTip}
                commentThreads={commentThreads}
                onCommentsClicked={
                  onCommentsClicked
                    ? () => {
                        onCommentsClicked(ProposalField.TeamLeader, leader.fullName);
                      }
                    : undefined
                }
              />
            )}
          </>
        }
        disableGutters
      >
        <ListItemAvatar>
          <IndividualAvatar individual={leader} session={session} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink 
                onClick={() => openUserProfileDialog(leader.id)}
              >
                {leader.fullName}
              </TeamMemberLink>
            </Typography>
          }
        ></ListItemText>
      </ListItem>
    );
  }

  /**
   * Renders a single vendor-team member's info as a list item
   *
   * @param member The member's info to render
   * @param allowDelete Whether or not to allow the member to be deleted
   */
  function renderMember(member: Individual, allowDelete?: boolean) {
    return (
      <ListItem
        key={member.userId.value ?? Guid.generate().value}
        secondaryAction={
          <>
            {allowDelete && (
              <Tooltip title="Remove Member">
                <span>
                  <IconButton 
                    onClick={() => handleMemberRemoved(member.userId)} 
                    size="medium"
                  >
                    <RemoveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!hideCommentButton && (
              <CommentsButton
                field={ProposalField.TeamMember(member.id)}
                isActive={activeReviewField?.isEqualTo(ProposalField.TeamMember(member.userId))}
                toolTip={commentsToolTip}
                disabled={disableComments}
                commentThreads={commentThreads}
                onCommentsClicked={
                  onCommentsClicked
                    ? () => {
                        onCommentsClicked(ProposalField.TeamMember(member.userId), member.fullName);
                      }
                    : undefined
                }
              />
            )}
          </>
        }
        disableGutters
      >
        <ListItemAvatar>
          <IndividualAvatar individual={member} session={session} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink 
                onClick={() => openUserProfileDialog(member.id)}
              >
                {member.fullName}
              </TeamMemberLink>
            </Typography>
          }
        ></ListItemText>
      </ListItem>
    );
  }

  function handleMarketplaceTeamSelected(team: MarketplaceTeam) {
    if (!team.leader.profile) throw new Error("Team leader profile is undefined");

    onMarketplaceTeamSelected?.(team);
    popDialog();
  }

  return (
    <MainContainer className={className}>
      {userPermissions?.includesAccessToMarketplaceSearch &&
        !disableEditing &&
        session.accountType === AccountType.Client && (
          <FromMarketplaceButton
            variant="outlined"
            size="small"
            color="primary"
            onClick={beginMarketplaceTeamSelection}
          >
            From Marketplace Team
          </FromMarketplaceButton>
        )}
      <TeamContainer>
        <TeamList dense>
          <TeamListSubheader>
            <span>Team Leader</span>
          </TeamListSubheader>
          {!selectedLeader && !session.context?.viewingAsVendor && (
            <>
              {!disableEditing && (!individualQuery.isLoading || !leader) && (
                <ListItemButton 
                  dense 
                  disableGutters 
                  disabled={!onLeaderUpdated} 
                  onClick={beginLeaderSelection}
                >
                  <ListItemAvatar>
                    <AddIcon color="primary" />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography color="primary">Select Leader</Typography>
                  </ListItemText>
                </ListItemButton>
              )}
              {!(individualQuery.isLoading && leader) && (
                <ListItem disableGutters>
                  <NoRowsPlaceholder color="textDisabled">No Leader</NoRowsPlaceholder>
                </ListItem>
              )}
            </>
          )}
          {!session.context?.viewingAsVendor && selectedLeader && renderLeader(selectedLeader, true)}
          {individualQuery.isLoading && !session.context?.viewingAsVendor && !selectedLeader && leader && (
            <TeamLoader text="Loading..." />
          )}
          {session.context?.viewingAsVendor && (!individualQuery.isLoading || !leader) && (
            <>
              <TeamLeaderInput
                disabled={teamLeaderSelectorAnchor !== undefined || disableEditing}
                label={selectedLeader ? "Team Leader" : "Select Team Leader"}
                placeholder="No Leader Selected"
                helperText="required"
                required
                autoComplete="off"
                error={!selectedLeader}
                onClick={handleSelectTeamLeaderClicked}
                value={selectedLeader ? `${selectedLeader?.fullName}` : ""}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  },
                }}
              />
              <TeamLeaderSelector
                popoverAnchor={teamLeaderSelectorAnchor}
                teamLeaderUserId={selectedLeader?.userId}
                onTeamLeaderUpdated={handleCoworkerSelectedAsLeader}
                onPopoverClose={() => {
                  setTeamLeaderSelectorAnchor(undefined);
                }}
              />
            </>
          )}
        </TeamList>
        <TeamList dense>
          <TeamListSubheader>
            <span>Team Members</span>
            <div>
              {selectedMembers && selectedMembers.length > 0 && !disableEditing && (
                <Tooltip title="Remove All Members">
                  <span>
                    <IconButton onClick={handleRemoveAllMembers}>
                      <RemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!hideCommentButton && (
                <CommentsButton
                  field={ProposalField.Team}
                  isActive={activeReviewField?.isEqualTo(ProposalField.Team)}
                  toolTip={commentsToolTip}
                  disabled={disableComments}
                  commentThreads={commentThreads}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Team);
                        }
                      : undefined
                  }
                />
              )}
            </div>
          </TeamListSubheader>
          {!disableEditing && !(individualQuery.isLoading && members.length) && (
            <ListItemButton dense disableGutters onClick={beginMemberSelection}>
              <ListItemAvatar>
                <AddIcon color="primary" />
              </ListItemAvatar>
              <ListItemText>
                <Typography color="primary">Add Member</Typography>
              </ListItemText>
            </ListItemButton>
          )}
          {((!individualQuery.isLoading && !selectedMembers) || members.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder color="textDisabled">No Members</NoRowsPlaceholder>
            </ListItem>
          )}
          {((!selectedMembers.length && !!members.length && individualQuery.isLoading) || undefined) && (
            <TeamLoader text="Loading..." />
          )}
          {selectedMembers?.map((member) => renderMember(member, !disableEditing))}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
